'use client';
import React from 'react';
import { cn } from '@/core/utils/cn';
import { BlockProps } from './types';

export function SeparatorBlock({
  block,
  currentStyles,
}: BlockProps) {
  const width = currentStyles.width !== undefined ? currentStyles.width : 100;
  const align = currentStyles.textAlign || 'center';

  const thickness = currentStyles.thickness ?? block.settings?.thickness ?? 2;
  const separatorType = block.settings?.separatorType || 'solid';
  const color = block.settings?.color || '#cbd5e1';
  const useGradient = block.settings?.useGradient || false;
  const gradientFrom = block.settings?.gradientFrom || '#3b82f6';
  const gradientTo = block.settings?.gradientTo || '#8b5cf6';
  const gradientAngle = block.settings?.gradientAngle || 90;

  const lineStyles: React.CSSProperties = {};

  if (separatorType === 'solid') {
    lineStyles.height = `${thickness}px`;
    if (useGradient) {
      lineStyles.backgroundImage = `linear-gradient(${gradientAngle}deg, ${gradientFrom}, ${gradientTo})`;
    } else {
      lineStyles.backgroundColor = color;
    }
  } else if (separatorType === 'dashed') {
    if (useGradient) {
      lineStyles.height = `${thickness}px`;
      lineStyles.backgroundImage = `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`;
      lineStyles.maskImage = 'repeating-linear-gradient(90deg, black, black 12px, transparent 12px, transparent 20px)';
      lineStyles.WebkitMaskImage = 'repeating-linear-gradient(90deg, black, black 12px, transparent 12px, transparent 20px)';
    } else {
      lineStyles.borderBottom = `${thickness}px dashed ${color}`;
      lineStyles.height = '0px';
    }
  } else if (separatorType === 'dotted') {
    if (useGradient) {
      lineStyles.height = `${thickness}px`;
      lineStyles.backgroundImage = `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`;
      lineStyles.maskImage = `radial-gradient(circle, black 40%, transparent 45%)`;
      lineStyles.WebkitMaskImage = `radial-gradient(circle, black 40%, transparent 45%)`;
      lineStyles.backgroundSize = `${thickness * 3}px ${thickness}px`;
      lineStyles.backgroundRepeat = 'repeat-x';
    } else {
      lineStyles.borderBottom = `${thickness}px dotted ${color}`;
      lineStyles.height = '0px';
    }
  } else if (separatorType === 'double') {
    if (useGradient) {
      lineStyles.height = `${thickness < 3 ? 3 : thickness}px`;
      lineStyles.backgroundImage = `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`;
      lineStyles.maskImage = 'linear-gradient(to bottom, black 33%, transparent 33%, transparent 66%, black 66%)';
      lineStyles.WebkitMaskImage = 'linear-gradient(to bottom, black 33%, transparent 33%, transparent 66%, black 66%)';
    } else {
      lineStyles.borderBottom = `${thickness < 3 ? 3 : thickness}px double ${color}`;
      lineStyles.height = '0px';
    }
  } else if (separatorType === 'wavy') {
    const activeColor = useGradient ? 'url(#grad)' : color;
    const gradDef = useGradient
      ? `<defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="${gradientFrom}"/><stop offset="100%" stop-color="${gradientTo}"/></linearGradient></defs>`
      : '';
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="16" viewBox="0 0 24 16">${gradDef}<path d="M0,8 Q6,2 12,8 T24,8" fill="none" stroke="${activeColor}" stroke-width="${thickness}" stroke-linecap="round"/></svg>`;
    const dataUri = `url('data:image/svg+xml;utf8,${encodeURIComponent(svgContent)}')`;
    lineStyles.height = '16px';
    lineStyles.backgroundImage = dataUri;
    lineStyles.backgroundRepeat = 'repeat-x';
    lineStyles.backgroundPosition = 'center';
  } else if (separatorType === 'zigzag') {
    const activeColor = useGradient ? 'url(#grad)' : color;
    const gradDef = useGradient
      ? `<defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="${gradientFrom}"/><stop offset="100%" stop-color="${gradientTo}"/></linearGradient></defs>`
      : '';
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16">${gradDef}<path d="M0,12 L10,4 L20,12" fill="none" stroke="${activeColor}" stroke-width="${thickness}" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const dataUri = `url('data:image/svg+xml;utf8,${encodeURIComponent(svgContent)}')`;
    lineStyles.height = '16px';
    lineStyles.backgroundImage = dataUri;
    lineStyles.backgroundRepeat = 'repeat-x';
    lineStyles.backgroundPosition = 'center';
  }

  return (
    <div
      className={cn(
        'w-full flex py-3 transition-all',
        align === 'left'
          ? 'justify-start'
          : align === 'right'
            ? 'justify-end'
            : 'justify-center'
      )}
    >
      <div
        style={{
          width: `${width}%`,
          ...lineStyles,
        }}
        className="transition-all"
      />
    </div>
  );
}
